import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import '../OrderInvoice/registerInvoiceFonts';

const C = { text: '#1a1a1a', muted: '#6b6b6b', line: '#e2e2e2', headBg: '#f2f2f2', rule: '#1a1a1a' };

const COL = { tick: 20, id: 48, customer: 115, address: 185, phone: 80, channel: 95, tracking: 85, price: 75, ready: 54 };

const HEADER_HEIGHT = 74;

const s = StyleSheet.create({
  page: { fontFamily: 'HindSiliguri', color: C.text, fontSize: 9, lineHeight: 1.35, paddingTop: HEADER_HEIGHT, paddingBottom: 44, paddingHorizontal: 42 },
  header: { position: 'absolute', top: 28, left: 42, right: 42 },
  titleRow: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', borderBottomWidth: 1.2, borderBottomColor: C.rule, paddingBottom: 5 },
  title: { fontSize: 13, fontWeight: 700 },
  window: { fontSize: 8.5, color: C.muted },
  headRow: { flexDirection: 'row', backgroundColor: C.headBg, paddingVertical: 4, paddingHorizontal: 3, marginTop: 6 },
  head: { fontSize: 7.5, fontWeight: 700, letterSpacing: 0.4, color: C.muted, textTransform: 'uppercase' },
  row: { flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 3, borderBottomWidth: 0.5, borderBottomColor: C.line },
  rowFirst: { marginTop: 2 },
  cell: { paddingRight: 6 },
  id: { fontWeight: 700 },
  muted: { color: C.muted },
  tick: { width: 11, height: 11, borderWidth: 0.8, borderColor: C.muted, borderRadius: 1.5 },
  footer: { position: 'absolute', bottom: 24, left: 42, right: 42, flexDirection: 'row', justifyContent: 'space-between', fontSize: 7.5, color: C.muted, borderTopWidth: 0.5, borderTopColor: C.line, paddingTop: 5 },
});

const money = (n) => 'Tk ' + Math.round(Number(n) || 0).toLocaleString('en-US');

const Row = ({ row, index }) => (
  <View style={[s.row, index === 0 && s.rowFirst]} wrap={false}>
    <View style={{ width: COL.tick }}><View style={s.tick} /></View>
    <Text style={[s.cell, s.id, { width: COL.id }]}>#{row.id}</Text>
    <Text style={[s.cell, { width: COL.customer }]}>{row.customerName}</Text>
    <Text style={[s.cell, { width: COL.address }]}>{row.address}</Text>
    <Text style={[s.cell, { width: COL.phone }]}>{row.phone}</Text>
    <Text style={[s.cell, { width: COL.channel }]}>{row.channelName || '—'}</Text>
    <Text style={[s.cell, { width: COL.tracking }]}>{row.trackingCode || '—'}</Text>
    <Text style={[s.cell, { width: COL.price, textAlign: 'right' }]}>{money(row.totalPrice)}</Text>
    <Text style={[s.cell, s.muted, { width: COL.ready, textAlign: 'right' }]}>{timeOnly(row.becameReady)}</Text>
  </View>
);

const timeOnly = (becameReady) => (becameReady || '').split(' ')[1] || '—';
const fmt = (d) => (d ? dayjs(d).format('DD MMM YYYY, HH:mm') : '');

export const filterByChannels = (rows = [], channelIds = []) => {
  if (!channelIds?.length) return rows;
  const wanted = new Set(channelIds.map(String));
  return rows.filter((r) => wanted.has(String(r.channelId)));
};

export const courierSheetCount = (rows, channelIds) => filterByChannels(rows, channelIds).length;

const CourierRunSheetDocument = ({ rows = [], channelIds = [], from, to }) => {
  const filtered = filterByChannels(rows, channelIds);
  const total = filtered.length;
  
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={s.page}>
        <View style={s.header} fixed>
          <View style={s.titleRow}>
            <Text style={s.title}>Courier delivery run sheet</Text>
            <Text style={s.window}>
              {total} order{total === 1 ? '' : 's'} · ready between {fmt(from)} and {fmt(to)}
            </Text>
          </View>
          <View style={s.headRow}>
            <Text style={[s.head, { width: COL.tick }]}> </Text>
            <Text style={[s.head, { width: COL.id }]}>Order</Text>
            <Text style={[s.head, { width: COL.customer }]}>Customer</Text>
            <Text style={[s.head, { width: COL.address }]}>Address</Text>
            <Text style={[s.head, { width: COL.phone }]}>Phone</Text>
            <Text style={[s.head, { width: COL.channel }]}>Channel</Text>
            <Text style={[s.head, { width: COL.tracking }]}>Tracking</Text>
            <Text style={[s.head, { width: COL.price, textAlign: 'right' }]}>Price</Text>
            <Text style={[s.head, { width: COL.ready, textAlign: 'right' }]}>Ready</Text>
          </View>
        </View>
        
        {filtered.map((row, index) => (
          <Row key={row.id} row={row} index={index} />
        ))}
        
        <View style={s.footer} fixed>
          <Text>Courier delivery run sheet</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default CourierRunSheetDocument;
